import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { intersperse } from '../../service/Intersperse';
import { LogHelpers } from '../../service/LogHelpers/LogHelpers';
import {
    ChangedOptions,
    TimeTargetGroups,
    TimeTargetingCorrection,
    TimeTargetingDayCorrectionsDisplay,
    TimeTargetingDayDisplay
} from '../../typings/log';
import {
    ExpandableCell,
    ExpandableProps
} from '../ExpandableCell/ExpandableCell';

import './TimeTargetingCell.css';
import * as keyset from './TimeTargetingCell.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

const modifiedKey = 'modified-row',
    addedKey = 'added-row';

export interface TimeTargetingProps extends ExpandableProps {
    diff: TimeTargetGroups;
}

export class TimeTargetingCell extends ExpandableCell<TimeTargetingProps> {
    protected getContent() {
        const {
                diff: { oldTimezone }
            } = this.props,
            timeTargetingWasOff = oldTimezone.id === '0';

        return (
            <div
                className={Bem.cls('timetargeting-cell')}
                ref={ref => (this.cell = ref)}
            >
                {this.getDiff(timeTargetingWasOff ? addedKey : modifiedKey)}
            </div>
        );
    }

    private getDiff = (key: string): React.ReactNode => {
        const {
            diff: { corrections }
        } = this.props;

        return (
            <div className={Bem.cls('timetargeting-cell', key)}>
                {this.getTime(key)}
                {this.getTimeZone()}
                {corrections.length > 0 ? this.getCorrectionDiv(key) : ''}
                {this.getHolidays()}
                {this.getWorkingWeekend()}
            </div>
        );
    };

    private getTime = (key: string): React.ReactNode => {
        return (
            <div className={classNames(Bem.cls('timetargeting-cell', 'row'))}>
                <span className={Bem.cls('timetargeting-cell', 'name')}>
                    {i18n('time')}:
                </span>
                {this.getTimeLine(key)}
            </div>
        );
    };

    private getTimeLine = (key: string): React.ReactNode => {
        const {
                diff: { time }
            } = this.props,
            expandedClassName = this.getExpandedClassName();
        return time.map((day: TimeTargetingDayDisplay, index1: number) => {
            const { hours } = day,
                dayKey = `${key}_day_${index1}`,
                hoursSpans = hours.map((entry, index2) => {
                    const { timeChangedType, hoursText } = entry;
                    return (
                        <span
                            key={`${dayKey}_${index2}`}
                            className={this.getDiffClassName(timeChangedType)}
                        >
                            {hoursText}
                        </span>
                    );
                });

            return (
                <span
                    key={dayKey}
                    className={classNames(
                        Bem.cls('timetargeting-cell', 'day'),
                        expandedClassName
                    )}
                >
                    <span>{`${day.daysText}: `}</span>
                    <span>{intersperse<JSX.Element>(', ', hoursSpans)}</span>
                </span>
            );
        });
    };

    private getTimeZone = (): React.ReactNode => {
        const {
                diff: {
                    timezone: { name: newName, id: newId },
                    oldTimezone: { name: oldName, id: oldId }
                }
            } = this.props,
            timeTargetingWasOff = oldId === '0',
            isTimezoneChanged = newId !== oldId,
            expandedClassName = this.getExpandedClassName();
        let span;

        if (timeTargetingWasOff) {
            span = (
                <span className={Bem.cls('timetargeting-cell', 'added')}>
                    {newName}
                </span>
            );
        } else if (isTimezoneChanged) {
            span = (
                <span>
                    <span className={Bem.cls('timetargeting-cell', 'removed')}>
                        {oldName}
                    </span>
                    &nbsp;
                    <span className={Bem.cls('timetargeting-cell', 'added')}>
                        {newName}
                    </span>
                </span>
            );
        } else {
            span = <span>{newName}</span>;
        }

        return (
            <div
                className={classNames(
                    Bem.cls('timetargeting-cell', 'row'),
                    expandedClassName
                )}
            >
                <span className={Bem.cls('timetargeting-cell', 'name')}>
                    {i18n('timezone')}:
                </span>
                {span}
            </div>
        );
    };

    private getCorrectionDiv = (key: string): React.ReactNode => {
        return (
            <div
                className={classNames(
                    Bem.cls('timetargeting-cell', 'row'),
                    Bem.cls('timetargeting-cell', 'corrections-cell')
                )}
            >
                <span
                    key={`${key}_corrections_name`}
                    className={Bem.cls('timetargeting-cell', 'name')}
                >
                    {i18n('time-correction')}:
                </span>
                {this.getCorrection(key)}
            </div>
        );
    };

    private getCorrection = (key: string): React.ReactNode => {
        const {
                diff: { corrections }
            } = this.props,
            expandedClassName = this.getExpandedClassName();
        return corrections.map(
            (day: TimeTargetingDayCorrectionsDisplay, index1: number) => {
                if (day.corrections.length > 0) {
                    const correctionKey = `${key}_correction_day_${index1}`,
                        correctionsSpans = day.corrections.map(
                            (entry, index2) => {
                                const innerCorrectionKey = `${correctionKey}${index2}`,
                                    correctionSpan = this.getCorrectionSpan(
                                        entry
                                    );
                                return (
                                    <span key={`${innerCorrectionKey}`}>
                                        {correctionSpan}
                                    </span>
                                );
                            }
                        );

                    return (
                        <span
                            key={correctionKey}
                            className={classNames(
                                Bem.cls('timetargeting-cell', 'day'),
                                expandedClassName
                            )}
                        >
                            <span>{`${day.daysText}: `}</span>
                            <span>
                                {intersperse<JSX.Element>(
                                    ', ',
                                    correctionsSpans
                                )}
                            </span>
                        </span>
                    );
                }
                return null;
            }
        );
    };

    private getCorrectionSpan = (
        entry: TimeTargetingCorrection
    ): React.ReactNode => {
        const {
            hoursText,
            correctionChangedType,
            correction,
            oldCorrection
        } = entry;
        if (correctionChangedType === ChangedOptions.modified) {
            return (
                <span>
                    {hoursText}
                    :&nbsp;
                    <span className={Bem.cls('timetargeting-cell', 'removed')}>
                        {oldCorrection}%
                    </span>
                    &nbsp;
                    <span className={Bem.cls('timetargeting-cell', 'added')}>
                        {correction}%
                    </span>
                </span>
            );
        } else {
            const className = Bem.cls(
                'timetargeting-cell',
                Bem.beminize(correctionChangedType)
            );

            return (
                <span className={className}>
                    {hoursText}
                    :&nbsp;
                    {correctionChangedType === ChangedOptions.removed
                        ? `${oldCorrection}%`
                        : `${correction}%`}
                </span>
            );
        }
    };

    private getWorkingWeekend = (): React.ReactNode => {
        const {
            diff: {
                workingWeekends: { oldWW, newWW }
            }
        } = this.props;

        if (oldWW === newWW && !oldWW) {
            return '';
        }

        const span =
            oldWW === newWW ? (
                <span
                    className={Bem.cls('timetargeting-cell', 'working-weekend')}
                >
                    {i18n('with-working-weekends')}
                </span>
            ) : (
                <span
                    className={Bem.cls('timetargeting-cell', 'working-weekend')}
                >
                    <span
                        className={Bem.cls(
                            'timetargeting-cell',
                            oldWW ? 'removed' : 'added'
                        )}
                    >
                        {i18n('with-working-weekends')}
                    </span>
                </span>
            );

        return (
            <div className={classNames(Bem.cls('timetargeting-cell', 'row'))}>
                {span}
            </div>
        );
    };

    private getHolidays = (): React.ReactNode => {
        const {
            diff: {
                holidays: { time }
            }
        } = this.props;

        if (time.length === 0) {
            return '';
        }

        const expandedClassName = this.getExpandedClassName();
        let holidaysOff = true;
        const hoursSpans = time.map((entry, index) => {
            const { timeChangedType, hoursText } = entry;
            holidaysOff =
                holidaysOff && timeChangedType === ChangedOptions.removed;
            return (
                <span
                    key={`holiday_${index}`}
                    className={this.getDiffClassName(timeChangedType)}
                >
                    {hoursText}
                </span>
            );
        });

        return (
            <div className={classNames(Bem.cls('timetargeting-cell', 'row'))}>
                <span className={Bem.cls('timetargeting-cell', 'name')}>
                    {i18n('holidays')}:
                </span>
                <span
                    key={'holiday'}
                    className={classNames(
                        Bem.cls('timetargeting-cell', 'holidays'),
                        expandedClassName
                    )}
                >
                    <span>{intersperse<JSX.Element>(', ', hoursSpans)}</span>
                    {/*при выключении праздников не надо писать про корректировку*/}
                    {holidaysOff ? '' : this.getHolidayCorrectionSpan()}
                </span>
            </div>
        );
    };

    private getHolidayCorrectionSpan = (): React.ReactNode => {
        const {
            diff: {
                holidays: { correctionChangeType, newCorrection, oldCorrection }
            }
        } = this.props;
        if (
            newCorrection === LogHelpers.noCorrectionValue &&
            oldCorrection === LogHelpers.noCorrectionValue
        ) {
            return '';
        }

        switch (correctionChangeType) {
            case ChangedOptions.notModified:
                return (
                    <span
                        className={Bem.cls(
                            'timetargeting-cell',
                            'holidays-click-value'
                        )}
                    >
                        {i18n('click-value', { value: `${oldCorrection}%` })}
                    </span>
                );
            case ChangedOptions.removed:
                return (
                    <span
                        className={classNames(
                            Bem.cls(
                                'timetargeting-cell',
                                'holidays-click-value'
                            ),
                            Bem.cls('timetargeting-cell', 'removed')
                        )}
                    >
                        {i18n('click-value', { value: `${oldCorrection}%` })}
                    </span>
                );
            case ChangedOptions.added:
                return (
                    <span
                        className={classNames(
                            Bem.cls(
                                'timetargeting-cell',
                                'holidays-click-value'
                            ),
                            Bem.cls('timetargeting-cell', 'added')
                        )}
                    >
                        {i18n('click-value', { value: `${newCorrection}%` })}
                    </span>
                );
            case ChangedOptions.modified:
                const span = (
                    <span>
                        <span
                            className={Bem.cls('timetargeting-cell', 'removed')}
                        >
                            {oldCorrection}%
                        </span>
                        &nbsp;
                        <span
                            className={Bem.cls('timetargeting-cell', 'added')}
                        >
                            {newCorrection}%
                        </span>
                    </span>
                );

                return (
                    <span
                        className={Bem.cls(
                            'timetargeting-cell',
                            'holidays-click-value'
                        )}
                    >
                        {i18nRaw('click-value', { value: span })}
                    </span>
                );
            default:
                throw Error(
                    `Неизвестный тип изменения во временном таргетинге: ${correctionChangeType}`
                );
        }
    };

    private getDiffClassName = (type: ChangedOptions): string => {
        return Bem.cls('timetargeting-cell', Bem.beminize(type));
    };

    private getExpandedClassName = (): string => {
        const { isExpanded, canExpand } = this.props;
        return !isExpanded && canExpand
            ? Bem.cls('expandable-cell', 'short-row')
            : '';
    };
}
